import {
	faAnglesLeft,
	faAnglesRight,
	faPuzzlePiece,
	faSitemap,
	faTrademark,
	faUserShield,
	faUsers
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type React from "react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo_desktop from "../assets/fynxt_blue.png";
import logo_icon from "../assets/loco-icon.png";
import { useSidebarStore } from "../components/SidebarStore";
import { useAuthStore } from "../store/auth.store";
import useIsMobile from "./UseIsMobile";

type SidebarProps = {
	collapsed: boolean;
	setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	isSuperAdmin: boolean;
	isBrand: boolean;
};

const SidebarLayout: React.FC<SidebarProps> = ({ isSuperAdmin }) => {
	const location = useLocation();
	const { brandId } = useAuthStore();
	const collapsed = useSidebarStore((state) => state.collapsed);
	const setCollapsed = useSidebarStore((state) => state.setCollapsed);
	const systemNavItems = [
		{ icon: faTrademark, label: "Brands", to: "/admin/brands" },
		{
			icon: faSitemap,
			label: "Integrations",
			to: "/admin/integrations"
		},
		{ icon: faUserShield, label: "Roles", to: "/admin/roles" },
		{ icon: faUsers, label: "Users", to: "/admin/users" }
	];

	const brandNavItems = [
		{ icon: faSitemap, label: "Integrations", to: `/${brandId}/integrations` },
		{
			icon: faPuzzlePiece,
			label: "Rule Engine",
			to: `/${brandId}/rule`
		},
		{ icon: faUserShield, label: "Roles", to: `/${brandId}/roles` },
		{ icon: faUsers, label: "Users", to: `/${brandId}/users` }
	];

	const navItems = isSuperAdmin ? systemNavItems : brandNavItems;

	const isMobile = useIsMobile();

	useEffect(() => {
		if (isMobile) {
			setCollapsed(true);
		}
	}, [isMobile, setCollapsed]);

	return (
		<aside
			className={`fixed inset-y-0 left-0 bg-[#002666] text-white transform transition-all duration-300 z-40 flex flex-col ${
				collapsed ? "w-16" : "w-64"
			}`}
		>
			<div className="flex items-center justify-between border-b border-white/10 bg-white">
				<div className="flex items-center gap-2 h-[50px]">
					{collapsed ? (
						<div className="flex justify-center items-center h-full px-4">
							<img src={logo_icon} alt="Mini Logo" className="h-6" />
						</div>
					) : (
						<div className="flex justify-center items-center h-full p-4">
							<img src={logo_desktop} alt="Full Logo" className="h-6" />
						</div>
					)}
				</div>
				<button
					type="button"
					onClick={() => setCollapsed(!collapsed)}
					className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 -m-[10px]"
				>
					<FontAwesomeIcon
						icon={collapsed ? faAnglesRight : faAnglesLeft}
						className={"text-base-500 text-blue-fynxt_1"}
					/>
				</button>
			</div>

			<nav className="flex-1 overflow-y-auto py-4">
				<ul className="space-y-1">
					{navItems.map((item) => (
						<li key={item.to}>
							<Link
								to={item.to}
								className={`flex items-center p-4 text-white text-sm ${
									location.pathname === item.to
										? "bg-white/10 font-medium"
										: "hover:bg-white/5"
								} ${collapsed ? "justify-center" : ""}`}
							>
								<FontAwesomeIcon icon={item.icon} className="w-5 h-5" />
								{!collapsed && (
									<span className="ml-3 whitespace-nowrap">{item.label}</span>
								)}
							</Link>
						</li>
					))}
				</ul>
			</nav>
		</aside>
	);
};

export default SidebarLayout;
